import type { RuleObject } from 'antd/es/form'
import type { ValidationError } from '../../../shared/verteoffice'
import { toBusinessDate } from '../businessDate'

export const requiredTrimmedRule = (msg: string): RuleObject => ({
  validator: (_, v) =>
    typeof v === 'string' && v.trim().length > 0
      ? Promise.resolve()
      : Promise.reject(new Error(msg)),
})

export const DEBUG_VO =
  typeof window !== 'undefined' &&
  (Boolean((window as any).__VO_DEBUG__) || window.localStorage?.getItem('__VO_DEBUG__') === '1')

export const normalizeDate = toBusinessDate

export function resolveOriginalData (data: any[] | (() => any[])): any[] {
  return typeof data === 'function' ? data() : data
}

export function pushRowInvalidFieldError (
  errors: ValidationError[],
  params: {
    sheetName: string
    index: number
    rowNumber: number
    record: any
    isUpdate: boolean
    field: string
    reason: string
  }
) {
  const { sheetName, index, rowNumber, record, isUpdate, field, reason } = params
  const existingError = errors.find(e => e.sheet === sheetName && e.rowNumber === rowNumber)
  if (existingError) {
    existingError.invalidFields.push({ field, reason })
    return
  }
  errors.push({
    sheet: sheetName,
    index,
    rowNumber,
    missingFields: [],
    invalidFields: [{ field, reason }],
    record,
    isUpdate
  })
}

export function getAuthorizationUserKey (record: any): string {
  const userId = typeof record?.user_id === 'string' ? record.user_id.trim() : ''
  if (userId) return `id:${userId}`

  const userName = typeof record?.user_name === 'string' ? record.user_name.trim().toUpperCase() : ''
  if (userName) return `name:${userName}`

  return ''
}

export function isExtraTimeChecked (value: unknown): boolean {
  if (value === true || value === 1) return true
  if (typeof value === 'string') {
    const normalized = value.trim().toUpperCase()
    return normalized === '1' || normalized === 'TRUE'
  }
  return false
}

export function getWorklogUserId (record: any, fallbackUserId = ''): string {
  const explicit = typeof record?.user_id === 'string' ? record.user_id.trim() : ''
  return explicit || fallbackUserId
}

interface AuthorizationSheetChanges {
  creations: any[]
  updates: Array<{ id: any; data: any }>
  deletions: Array<{ id: any }>
}

/**
 * Agrega en una sola pasada las horas autorizadas por `userId::fecha`,
 * aplicando el estado efectivo de la hoja de autorizaciones (base + cambios
 * pendientes del mismo lote de Guardar). Compartido por la regla de tope
 * diario y la de horas extra autorizadas.
 */
export function buildAuthorizedHoursByUserDate (
  authorizationData: any[],
  authorizationChanges?: AuthorizationSheetChanges
): Map<string, number> {
  const totals = new Map<string, number>()
  const originalById = new Map<string, any>(
    authorizationData.map((record: any) => [String(record.id), record])
  )
  const updatingIds = new Set((authorizationChanges?.updates ?? []).map(update => String(update.id)))
  const deletedIds = new Set((authorizationChanges?.deletions ?? []).map(deletion => String(deletion.id)))

  const add = (record: any) => {
    const date = normalizeDate(record?.date ?? record?.autorization_date)
    const userId = typeof record?.user_id === 'string' ? record.user_id.trim() : ''
    if (!date || !userId) return
    const key = `${userId}::${date}`
    totals.set(key, (totals.get(key) ?? 0) + Number(record.total_hours || 0))
  }

  authorizationData.forEach(record => {
    const id = String(record.id)
    if (updatingIds.has(id) || deletedIds.has(id)) return
    add(record)
  })
  authorizationChanges?.updates.forEach(update => {
    const original = originalById.get(String(update.id))
    add({ ...(original ?? {}), ...(update.data ?? {}) })
  })
  authorizationChanges?.creations.forEach(add)

  return totals
}

export function getAuthorizedHoursForUserDate (
  totals: Map<string, number>,
  date: string,
  userId: string
): number {
  const normalizedDate = normalizeDate(date)
  const trimmedUserId = typeof userId === 'string' ? userId.trim() : ''
  if (!normalizedDate || !trimmedUserId) return 0
  return totals.get(`${trimmedUserId}::${normalizedDate}`) ?? 0
}

/**
 * Tope de jornada ("daily_limit_hours") por `userId::fecha`, aplicando el
 * estado efectivo de la hoja de autorizaciones (base + cambios pendientes).
 * Si un día tiene varias filas con tope, gana el mayor.
 */
export function buildDailyLimitByUserDate (
  authorizationData: any[],
  authorizationChanges?: AuthorizationSheetChanges
): Map<string, number> {
  const limits = new Map<string, number>()
  const originalById = new Map<string, any>(
    authorizationData.map((record: any) => [String(record.id), record])
  )
  const updatingIds = new Set((authorizationChanges?.updates ?? []).map(update => String(update.id)))
  const deletedIds = new Set((authorizationChanges?.deletions ?? []).map(deletion => String(deletion.id)))

  const add = (record: any) => {
    const date = normalizeDate(record?.date ?? record?.autorization_date)
    const userId = typeof record?.user_id === 'string' ? record.user_id.trim() : ''
    const limit = Number(record?.daily_limit_hours)
    if (!date || !userId || !Number.isFinite(limit) || limit <= 0) return
    const key = `${userId}::${date}`
    limits.set(key, Math.max(limits.get(key) ?? 0, limit))
  }

  authorizationData.forEach(record => {
    const id = String(record.id)
    if (updatingIds.has(id) || deletedIds.has(id)) return
    add(record)
  })
  authorizationChanges?.updates.forEach(update => {
    const original = originalById.get(String(update.id))
    add({ ...(original ?? {}), ...(update.data ?? {}) })
  })
  authorizationChanges?.creations.forEach(add)

  return limits
}

export function getDailyLimitForUserDate (
  limits: Map<string, number>,
  date: string,
  userId: string
): number {
  const normalizedDate = normalizeDate(date)
  const trimmedUserId = typeof userId === 'string' ? userId.trim() : ''
  if (!normalizedDate || !trimmedUserId) return 0
  return limits.get(`${trimmedUserId}::${normalizedDate}`) ?? 0
}
